import React, { createContext, useState, useEffect, useCallback } from "react";

export const AuthContext = createContext();

export default function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  const role = user?.role || null;

  const initializeAuth = useCallback(async () => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    // console.log("🔄 AuthProvider: Initializing", {
    //   storedToken: storedToken ? "Exists" : "None",
    //   storedUser: storedUser ? "Exists" : "None"
    // });

    if (!storedToken || !storedUser) {
      // console.log("⚠️ AuthProvider: No stored credentials");
      setReady(true);
      return;
    }

    try {
      // First, set from localStorage immediately to prevent redirects
      const parsedUser = JSON.parse(storedUser);
      // console.log("👤 AuthProvider: User from localStorage", {
      //   role: parsedUser.role,
      //   id: parsedUser.id,
      //   name: parsedUser.name
      // });

      setToken(storedToken);
      setUser(parsedUser);

      // Validate token with backend WITHOUT overwriting user data
      const res = await fetch(
        "https://lms.childinfo.in/api/auth/me.php",
        {
          headers: { Authorization: "Bearer " + storedToken }
        }
      );

      if (!res.ok) {
        console.warn("❌ AuthProvider: Token validation failed");
        throw new Error("Invalid token");
      }

      const data = await res.json();
      // console.log("✅ AuthProvider: Token validated", data);

      // 🔥 CRITICAL FIX: Only update user if we have valid role from API
      // Don't overwrite with potentially incorrect data
      if (data.user && data.user.role && data.user.role === parsedUser.role) {
        // Roles match, update with fresh data
        // console.log("🔄 AuthProvider: Updating user with fresh API data");
        setUser(data.user);
      } else if (data.user && data.user.role) {
        // API returned different role - warn but keep localStorage role
        console.warn("⚠️ AuthProvider: Role mismatch!", {
          localStorageRole: parsedUser.role,
          apiRole: data.user.role
        });
        // Keep localStorage role - it's more reliable for your case
        // Or update only non-role fields:
        setUser({
          ...data.user,
          role: parsedUser.role // Keep the original role
        });
      }
      // If no user from API, keep localStorage data

    } catch (err) {
      console.warn("[AUTH] Invalid session:", err.message);
      // Only clear on network/validation errors, not on role mismatch
      if (err.message.includes("Invalid token") || err.message.includes("Failed to fetch")) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setToken(null);
        setUser(null);
      }
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = (jwt, userData) => {
    // console.log("🔑 AuthProvider: Login", { role: userData.role });
    localStorage.setItem("token", jwt);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("logintype", "web");

    setToken(jwt);
    setUser(userData);
    setReady(true);
  };

  const logout = () => {
    // console.log("🚪 AuthProvider: Logout");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("logintype");
    setToken(null);
    setUser(null);
    setReady(true);
  };

  return (
    <AuthContext.Provider
      value={{ token, user, role, ready, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}